import React from 'react';

import { useToggle } from 'react-use';
import classNames from 'classnames';

import classes from './NgzPromoCodesList.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { EmptyList } from '../../../shared/components/ui-components';
import { HeaderRow } from './HeaderRow/HeaderRow';
import { PromoCodeRow } from './PromoCodeRow/PromoCodeRow';
import { AddPromoCodeModal } from './AddPromoCodeModal/AddPromoCodeModal';
import { usePromoCodes } from './hooks/usePromoCodes';
import { useSort } from '../../../shared/hooks/useSort';
import { useFilterByField } from '../../../shared/hooks/useFilterByField';
import { LbHorizontalScrollContainer } from '../../../shared/components/LbHorizontalScrollContainer/LbHorizontalScrollContainer';
import { TitleBlock } from './TitleBlock/TitleBlock';
import { PromoCodeInfo } from '../../interfaces/promoCodeInfo';
import { PromoCodeStatus } from '../../constants/promoCodes';
import { scanIqPromoCodeService } from '../../services/promoCodesService/scanIqPromoCodeService';
import { nutritionPromoCodeService } from '../../services/promoCodesService/nutritionPromoCodeService';
import { challengePromoCodeService } from '../../services/promoCodesService/challengePromoCodeService';
import {
  SOLUTION_TYPE,
  SolutionType,
} from '../../../shared/constants/user-info/solutionType';
import { PromoCodesContextProvider } from './context/PromoCodesContext';
import { PromoCodesService } from '../../services/promoCodesService/promoCodesService';

type PromoCodesListProps = {
  targetId: number;
  solutionType: SolutionType;
};

const serviceConfig: Record<SolutionType, PromoCodesService> = {
  [SOLUTION_TYPE.SCAN_IQ]: scanIqPromoCodeService,
  [SOLUTION_TYPE.NUTRITION]: nutritionPromoCodeService,
  [SOLUTION_TYPE.CHALLENGE]: challengePromoCodeService,
};

const PromoCodesList = ({ targetId, solutionType }: PromoCodesListProps) => {
  const subscriptionTypeAvailable =
    solutionType === SOLUTION_TYPE.NUTRITION || solutionType === SOLUTION_TYPE.SCAN_IQ;

  const { promoCodes, subscriptions, loading, addPromoCode, togglePromoCode } =
    usePromoCodes(targetId, serviceConfig[solutionType], subscriptionTypeAvailable);

  const { filteredData, filterValue, handleSetFilterValue } = useFilterByField<
    PromoCodeInfo,
    'status',
    PromoCodeStatus
  >(promoCodes, 'status');

  const { sortedData: sortedPromoCodes, sortMethods } = useSort({
    data: filteredData,
    sortTypeConfig: { name: 'STRING' },
  });

  const [isShowAddPromoCodeModal, toggleShowAddPromoCodeModal] = useToggle(false);

  const tableClassName = classNames(classes.promoCodesTable, {
    [classes.withSubscriptionType]: subscriptionTypeAvailable,
  });

  return (
    <PromoCodesContextProvider
      solutionType={solutionType}
      subscriptionTypeAvailable={subscriptionTypeAvailable}
    >
      <TitleBlock
        filterValue={filterValue}
        onAddPromoCodeButtonClick={toggleShowAddPromoCodeModal}
        onChangeFilterValue={handleSetFilterValue}
      />
      <LbHorizontalScrollContainer
        staticContent={
          <EmptyList
            show={!sortedPromoCodes.length && !loading}
            isSearch={!!filterValue}
            description="There are no promo codes"
          />
        }
      >
        <div className={tableClassName}>
          <HeaderRow
            handleSort={sortMethods.handleSort}
            getIsSortedBy={sortMethods.getIsSortedBy}
            reset={sortMethods.reset}
          />
          {sortedPromoCodes.map((promoCodeInfo) => (
            <PromoCodeRow
              key={promoCodeInfo.id}
              promoCodeInfo={promoCodeInfo}
              onPromoCodeToggle={togglePromoCode}
            />
          ))}
        </div>
      </LbHorizontalScrollContainer>
      {isShowAddPromoCodeModal && (
        <AddPromoCodeModal
          subscriptions={subscriptions}
          onClose={toggleShowAddPromoCodeModal}
          addPromoCode={addPromoCode}
        />
      )}
    </PromoCodesContextProvider>
  );
};

angularizeWithProviders(PromoCodesList, 'ngzPromoCodesList', {
  targetId: '<',
  solutionType: '<',
});

export { PromoCodesList };
